import type {
  ChatAction,
  Currency,
  SalonService,
} from '../shared/contracts';
import type { MessageOptions } from './api';
import { formatDisplayDate, formatDuration, formatPrice } from './format';
import { getWidgetCopy } from './i18n';
import { CheckIcon } from './icons';

interface Props {
  action: ChatAction;
  disabled: boolean;
  locale: string;
  onPrompt: (
    message: string,
    options?: MessageOptions,
  ) => void;
}

function ServiceCard({
  service,
  currency,
  suggestedDate,
  emphasized = false,
  disabled,
  locale,
  onPrompt,
}: {
  service: SalonService;
  currency: Currency;
  suggestedDate?: string;
  emphasized?: boolean;
  disabled: boolean;
  locale: string;
  onPrompt: Props['onPrompt'];
}) {
  const copy = getWidgetCopy(locale);
  const suggestedDateLabel = suggestedDate
    ? formatDisplayDate(suggestedDate, locale)
    : '';

  return (
    <button
      class={`bc-service-card${emphasized ? ' bc-service-card-primary' : ''}`}
      type="button"
      disabled={disabled}
      onClick={() =>
        onPrompt(
          suggestedDate
            ? copy.bookServiceOn(service.name, suggestedDateLabel)
            : copy.bookServiceEarliest(service.name),
          {
            serviceId: service.id,
            ...(suggestedDate ? { date: suggestedDate } : {}),
          },
        )
      }
    >
      <span>
        <strong>{service.name}</strong>
        <small>{formatDuration(service.durationMinutes, locale)}</small>
      </span>
      <span class="bc-service-price">
        <small>{copy.from}</small>
        <strong>{formatPrice(service.minPrice, currency, locale)}</strong>
      </span>
    </button>
  );
}

export function ActionCards({
  action,
  disabled,
  locale,
  onPrompt,
}: Props) {
  const copy = getWidgetCopy(locale);

  if (action.type === 'services') {
    const primaryService = action.primaryServiceId
      ? action.services.find(
          (service) => service.id === action.primaryServiceId,
        )
      : undefined;
    const relatedServices = primaryService
      ? action.services.filter((service) => service.id !== primaryService.id)
      : action.services;

    return (
      <div class="bc-service-list">
        {primaryService && (
          <ServiceCard
            service={primaryService}
            currency={action.currency}
            suggestedDate={action.suggestedDate}
            emphasized
            disabled={disabled}
            locale={locale}
            onPrompt={onPrompt}
          />
        )}
        {primaryService && relatedServices.length > 0 && (
          <div class="bc-related-services-label">
            {copy.relatedServices}
          </div>
        )}
        {relatedServices.map((service) => (
          <ServiceCard
            key={service.id}
            service={service}
            currency={action.currency}
            suggestedDate={action.suggestedDate}
            disabled={disabled}
            locale={locale}
            onPrompt={onPrompt}
          />
        ))}
      </div>
    );
  }

  if (action.type === 'slots') {
    return (
      <div class="bc-slot-card">
        <div class="bc-slot-card-head">
          <span>
            <small>{copy.availableOn}</small>
            <strong>{formatDisplayDate(action.date, locale)}</strong>
          </span>
          <span>{formatDuration(action.service.durationMinutes, locale)}</span>
        </div>
        {action.groups.length === 0 && (
          <div class="bc-empty-slots">
            <span>{copy.noTimesAvailable}</span>
            <button
              type="button"
              disabled={disabled}
              onClick={() =>
                onPrompt(
                  copy.nextAvailablePrompt(action.service.name),
                  { serviceId: action.service.id },
                )
              }
            >
              {copy.findNextAvailable}
            </button>
          </div>
        )}
        {action.groups.map((group) => (
          <div class="bc-master-group" key={group.masterId}>
            <div class="bc-master">
              {group.masterAvatarUrl ? (
                <img src={group.masterAvatarUrl} alt="" />
              ) : (
                <span>{group.masterName.charAt(0).toUpperCase()}</span>
              )}
              <div>
                <small>{copy.professional}</small>
                <strong>{group.masterName}</strong>
              </div>
            </div>
            <div class="bc-times">
              {group.slots.map((slot) => (
                <button
                  type="button"
                  key={slot.token}
                  disabled={disabled}
                  onClick={() =>
                    onPrompt(
                      copy.chooseSlotPrompt(slot.time, slot.masterName),
                      { slotToken: slot.token },
                    )
                  }
                >
                  <strong>{slot.time}</strong>
                  <small>
                    {formatPrice(slot.price, action.currency, locale)}
                  </small>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (action.type === 'booking_confirmed') {
    return (
      <div class="bc-confirmation">
        <span class="bc-confirmation-icon"><CheckIcon /></span>
        <div>
          <small>
            {action.simulated
              ? copy.localTestBooking
              : copy.bookingConfirmed}
          </small>
          <strong>{action.draft.slot.serviceName}</strong>
          <span>
            {formatDisplayDate(action.draft.slot.date, locale)} {copy.at}{' '}
            {action.draft.slot.time}
            {' · '}
            {action.draft.slot.masterName}
          </span>
          <code>{action.bookingId}</code>
        </div>
      </div>
    );
  }

  return null;
}
